export const MAX_PROFILE_PICTURE_BYTES = 10_000_000;

export const ACCEPTED_PROFILE_PICTURE_MIME_TYPES = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/**
 * Validate a user-selected source file before opening the crop modal.
 * @returns {{ ok: true } | { ok: false, reason: 'type' | 'size' | 'decode' }}
 */
export function validateProfilePictureSource(file) {
  if (!file || typeof file !== 'object') {
    return { ok: false, reason: 'type' };
  }

  if (!ACCEPTED_PROFILE_PICTURE_MIME_TYPES.includes(file.type)) {
    return { ok: false, reason: 'type' };
  }

  if (typeof file.size === 'number' && file.size > MAX_PROFILE_PICTURE_BYTES) {
    return { ok: false, reason: 'size' };
  }

  return { ok: true };
}

/**
 * Decode an image Blob/File. Rejects undecodable sources.
 * The returned image keeps a live object URL on `image.src` for crop preview;
 * callers must revoke via `revokeDecodedProfilePicture(image)`.
 * @returns {Promise<HTMLImageElement>}
 */
export function decodeProfilePictureSource(
  file,
  createObjectUrl = URL.createObjectURL,
  revokeObjectUrl = URL.revokeObjectURL,
) {
  return new Promise((resolve, reject) => {
    const url = createObjectUrl(file);
    const image = new Image();
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        revokeObjectUrl(url);
        reject(new Error('undecodable'));
        return;
      }
      image.__profilePictureObjectUrl = url;
      image.__revokeObjectUrl = revokeObjectUrl;
      resolve(image);
    };
    image.onerror = () => {
      revokeObjectUrl(url);
      reject(new Error('undecodable'));
    };
    image.src = url;
  });
}

export function revokeDecodedProfilePicture(image) {
  if (!image) return;
  const url = image.__profilePictureObjectUrl;
  const revoke = image.__revokeObjectUrl || URL.revokeObjectURL;
  if (url) {
    revoke(url);
    image.__profilePictureObjectUrl = null;
  }
}
